/**
 * Pure client-side view-filter helpers for Arbitrage / Surebets.
 * Independent from API bookmaker recalculation (FilterContext).
 * No React, no localStorage — safe to unit-test.
 *
 * Filter semantics:
 * - minMargin: trim margins to value >= threshold; drop card if none left; 0 = no filter
 * - searchQuery: case-insensitive match on teamOne, teamTwo, championship; blank = no search
 * - selectedChampionships: null = all; array = event.championship must be in list (empty => none)
 * - selectedMarketKeys: null = all; array = trim margins to those keys (empty => none)
 * - timeWindow: 'all' | 'today' | 'next24h' | 'next48h' (client local timezone)
 */

/** Min-margin chip thresholds used by the arbitrage UI (0 = "All"). */
export const MARGIN_CHIP_VALUES = [0, 3, 5, 7, 10];

/** Default view-filter state (no restrictions). */
export const DEFAULT_SUREBET_VIEW_FILTERS = Object.freeze({
  minMargin: 0,
  searchQuery: '',
  selectedChampionships: null,
  selectedMarketKeys: null,
  timeWindow: 'all'
});

/** Allowed timeWindow preset ids (same as Best Odds). */
export const TIME_WINDOW_VALUES = Object.freeze(['all', 'today', 'next24h', 'next48h']);

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Format a Date as YYYY-MM-DD in the client's local timezone.
 * @param {Date} date
 * @returns {string}
 */
export const formatLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse event.date + event.time as a kickoff Date in the CLIENT LOCAL timezone.
 * @param {Object} event
 * @returns {Date|null}
 */
export const parseEventKickoffLocal = (event) => {
  const dateStr = event?.date;
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
    return null;
  }

  const [year, month, day] = String(dateStr).split('-').map(Number);
  const timeStr = event?.time ? String(event.time).trim() : '00:00';
  const timeMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timeStr);
  const hours = timeMatch ? Number(timeMatch[1]) : 0;
  const minutes = timeMatch ? Number(timeMatch[2]) : 0;
  const seconds = timeMatch && timeMatch[3] ? Number(timeMatch[3]) : 0;

  if (
    !Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)
    || !Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)
  ) {
    return null;
  }

  const kickoff = new Date(year, month - 1, day, hours, minutes, seconds);
  return Number.isNaN(kickoff.getTime()) ? null : kickoff;
};

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const normalizeSearchText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().toLowerCase();
};

/**
 * Shallow-clone a surebet with a replaced margins object.
 * @param {Object} surebet
 * @param {Object} margins
 * @returns {Object}
 */
const withMargins = (surebet, margins) => ({
  ...surebet,
  margins
});

/**
 * Keep surebets matching searchQuery on teams or championship.
 * @param {Array} surebets
 * @param {string|null|undefined} searchQuery
 * @returns {Array}
 */
export const applySearch = (surebets, searchQuery) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  const query = normalizeSearchText(searchQuery);
  if (!query) {
    return surebets;
  }

  return surebets.filter((item) => {
    const event = item?.event || {};
    const haystack = [
      event.teamOne,
      event.teamTwo,
      event.championship
    ]
      .map(normalizeSearchText)
      .join(' ');

    return haystack.includes(query);
  });
};

/**
 * Variant B: trim margins below minMargin; drop card if none remain.
 * @param {Array} surebets
 * @param {number|null|undefined} minMargin
 * @returns {Array}
 */
export const applyMinMarginWithTrim = (surebets, minMargin) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  const threshold = Number(minMargin);
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return surebets;
  }

  const result = [];

  surebets.forEach((item) => {
    const margins = item?.margins && typeof item.margins === 'object'
      ? item.margins
      : {};

    const trimmed = {};
    Object.entries(margins).forEach(([key, margin]) => {
      if (Number(margin?.value) >= threshold) {
        trimmed[key] = margin;
      }
    });

    if (Object.keys(trimmed).length > 0) {
      result.push(withMargins(item, trimmed));
    }
  });

  return result;
};

/**
 * Keep surebets whose event.championship is in the selected list.
 * @param {Array} surebets
 * @param {string[]|null|undefined} selectedChampionships
 * @returns {Array}
 */
export const applyChampionships = (surebets, selectedChampionships) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  if (selectedChampionships === null || selectedChampionships === undefined) {
    return surebets;
  }

  if (!Array.isArray(selectedChampionships)) {
    return surebets;
  }

  if (selectedChampionships.length === 0) {
    return [];
  }

  const allowed = new Set(
    selectedChampionships
      .filter((name) => name !== null && name !== undefined && String(name).trim().length > 0)
      .map((name) => String(name))
  );

  if (allowed.size === 0) {
    return [];
  }

  return surebets.filter((item) => allowed.has(String(item?.event?.championship || '')));
};

/**
 * Trim margins to selected market keys; drop card if none remain.
 * null/undefined = no market filter.
 * @param {Array} surebets
 * @param {string[]|null|undefined} selectedMarketKeys
 * @returns {Array}
 */
export const applyMarketKeys = (surebets, selectedMarketKeys) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  if (selectedMarketKeys === null || selectedMarketKeys === undefined) {
    return surebets;
  }

  if (!Array.isArray(selectedMarketKeys)) {
    return surebets;
  }

  if (selectedMarketKeys.length === 0) {
    return [];
  }

  const allowed = new Set(
    selectedMarketKeys
      .filter((key) => key !== null && key !== undefined && String(key).trim().length > 0)
      .map((key) => String(key))
  );

  if (allowed.size === 0) {
    return [];
  }

  const result = [];

  surebets.forEach((item) => {
    const margins = item?.margins && typeof item.margins === 'object'
      ? item.margins
      : {};

    const trimmed = {};
    Object.entries(margins).forEach(([key, margin]) => {
      if (allowed.has(String(key))) {
        trimmed[key] = margin;
      }
    });

    if (Object.keys(trimmed).length > 0) {
      result.push(withMargins(item, trimmed));
    }
  });

  return result;
};

/**
 * Keep surebets whose kickoff falls in the selected time window.
 * Timezone: CLIENT LOCAL. Pass `now` for deterministic tests.
 * @param {Array} surebets
 * @param {string|null|undefined} timeWindow
 * @param {Date} [now]
 * @returns {Array}
 */
export const applyTimeWindow = (surebets, timeWindow, now = new Date()) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  const windowId = TIME_WINDOW_VALUES.includes(timeWindow)
    ? timeWindow
    : DEFAULT_SUREBET_VIEW_FILTERS.timeWindow;

  if (windowId === 'all') {
    return surebets;
  }

  if (windowId === 'today') {
    const todayKey = formatLocalDateKey(now);
    return surebets.filter((item) => String(item?.event?.date || '') === todayKey);
  }

  if (windowId === 'next24h' || windowId === 'next48h') {
    const hours = windowId === 'next48h' ? 48 : 24;
    const end = new Date(now.getTime() + hours * MS_PER_HOUR);
    return surebets.filter((item) => {
      const kickoff = parseEventKickoffLocal(item?.event);
      if (!kickoff) {
        return false;
      }
      return kickoff >= now && kickoff <= end;
    });
  }

  return surebets;
};

/**
 * Apply all view filters.
 * Order: search → minMargin (trim) → championships → markets (trim) → timeWindow.
 *
 * @param {Array} surebets
 * @param {Object} [filters]
 * @param {Date} [now]
 * @returns {Array}
 */
export const filterSurebetsView = (surebets, filters = {}, now = new Date()) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  const {
    minMargin = DEFAULT_SUREBET_VIEW_FILTERS.minMargin,
    searchQuery = DEFAULT_SUREBET_VIEW_FILTERS.searchQuery,
    selectedChampionships = DEFAULT_SUREBET_VIEW_FILTERS.selectedChampionships,
    selectedMarketKeys = DEFAULT_SUREBET_VIEW_FILTERS.selectedMarketKeys,
    timeWindow = DEFAULT_SUREBET_VIEW_FILTERS.timeWindow
  } = filters || {};

  let result = surebets;
  result = applySearch(result, searchQuery);
  result = applyMinMarginWithTrim(result, minMargin);
  result = applyChampionships(result, selectedChampionships);
  result = applyMarketKeys(result, selectedMarketKeys);
  result = applyTimeWindow(result, timeWindow, now);
  return result;
};

/**
 * Whether any view filter currently restricts the list.
 * @param {Object} [filters]
 * @returns {boolean}
 */
export const hasActiveSurebetViewFilters = (filters = {}) => {
  const {
    minMargin = 0,
    searchQuery = '',
    selectedChampionships = null,
    selectedMarketKeys = null,
    timeWindow = 'all'
  } = filters || {};

  if (Number(minMargin) > 0) {
    return true;
  }

  if (normalizeSearchText(searchQuery)) {
    return true;
  }

  if (Array.isArray(selectedChampionships)) {
    return true;
  }

  if (Array.isArray(selectedMarketKeys)) {
    return true;
  }

  if (timeWindow && timeWindow !== 'all' && TIME_WINDOW_VALUES.includes(timeWindow)) {
    return true;
  }

  return false;
};

/**
 * Unique championship names from the current surebets list (sorted).
 * @param {Array} surebets
 * @returns {string[]}
 */
export const collectChampionships = (surebets) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  const ids = new Set();
  surebets.forEach((item) => {
    const name = item?.event?.championship;
    if (name && String(name).trim()) {
      ids.add(String(name));
    }
  });

  return Array.from(ids).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

/**
 * Unique margin/market keys present in the current surebets list (sorted).
 * @param {Array} surebets
 * @returns {string[]}
 */
export const collectMarketKeys = (surebets) => {
  if (!Array.isArray(surebets)) {
    return [];
  }

  const keys = new Set();
  surebets.forEach((item) => {
    const margins = item?.margins && typeof item.margins === 'object'
      ? item.margins
      : {};
    Object.keys(margins).forEach((key) => {
      if (key) {
        keys.add(String(key));
      }
    });
  });

  return Array.from(keys).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

export default {
  MARGIN_CHIP_VALUES,
  DEFAULT_SUREBET_VIEW_FILTERS,
  TIME_WINDOW_VALUES,
  formatLocalDateKey,
  parseEventKickoffLocal,
  normalizeSearchText,
  applySearch,
  applyMinMarginWithTrim,
  applyChampionships,
  applyMarketKeys,
  applyTimeWindow,
  filterSurebetsView,
  hasActiveSurebetViewFilters,
  collectChampionships,
  collectMarketKeys
};
